import {
  SignedIn,
  SignedOut,
  RedirectToSignIn,
  useOrganization,
  useOrganizationList,
  useClerk,
  useUser,
  useAuth,
} from '@clerk/clerk-react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { Spinner } from 'reactstrap';
import * as Sentry from '@sentry/react';
import AppLayout from './components/layout/appLayout';
import SignInPage from './pages/signInPage';
import SignUpPage from './pages/signUpPage';
import SubscribePage from './pages/subscribePage';
import DashboardPage from './pages/dashboardPage';
import CustomersPage from './pages/customersPage';
import ViewCustomerPage from './pages/viewCustomerPage';
import QuotesPage from './pages/quotesPage';
import ViewQuotePage from './pages/viewQuotePage';
import JobsPage from './pages/jobsPage';
import ViewJobPage from './pages/viewJobPage';
import SettingsPage from './pages/settingsPage';
import HelpPage from './pages/helpPage';
import OnboardingPage from './pages/onboardingPage';
import QuoteResponsePage from './pages/quoteResponsePage';
import useBillingStatus from './hooks/useBillingStatus';

const LoadingSpinner = () => (
  <div className="d-flex align-items-center justify-content-center" style={{ height: '100vh' }}>
    <Spinner style={{ width: '2.5rem', height: '2.5rem' }} />
  </div>
);

// Mirrors the mobile SentryUserSync — keeps Sentry's user context in step with
// Clerk so every captured error is tagged with the user it happened to. Lives
// inside the Clerk provider tree because that's where the hooks resolve.
function SentryUserSync() {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();

  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn && user) {
      Sentry.setUser({
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [isLoaded, isSignedIn, user]);

  return null;
}

// Promotes the first available membership to active when one exists but
// none is selected (edge case: stale session, or a freshly-accepted Worker
// invite that didn't auto-set as active). When the user has no memberships
// at all — i.e. they've just signed up — we route to /onboarding to drive
// the first-tenant creation flow. This replaces Clerk's hosted "Force
// Organization Membership" task; we disabled that in the Clerk Dashboard
// alongside locking down user-driven org creation.
function RequireOrg({ children }) {
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const { setActive, userMemberships, isLoaded: listLoaded } = useOrganizationList({
    userMemberships: true,
  });

  useEffect(() => {
    if (!listLoaded || organization) return;
    const first = userMemberships?.data?.[0];
    if (first && setActive) setActive({ organization: first.organization.id });
  }, [listLoaded, organization, userMemberships, setActive]);

  if (!orgLoaded || !listLoaded) return null;
  if (organization) return children;
  // No memberships → first-time signup → route to onboarding.
  if (!userMemberships?.data?.length) return <Navigate to="/onboarding" replace />;
  return null; // memberships exist, waiting for setActive to land
}

// Gates the main app behind an active subscription. The /subscribe page
// lives outside this guard so users can reach it to pick a plan.
function RequireSubscription({ children }) {
  const { data, isLoading } = useBillingStatus();

  if (isLoading) return <LoadingSpinner />;
  if (!data?.hasActiveSubscription) return <Navigate to="/subscribe" replace />;
  return children;
}

const IS_SATELLITE = import.meta.env.VITE_CLERK_IS_SATELLITE === 'true';
const CLERK_DOMAIN = import.meta.env.VITE_CLERK_DOMAIN;

// In satellite mode, Clerk's sync sets the session cookie but ClerkProvider
// doesn't re-evaluate in time. Reload once so Clerk picks up the cookie on
// a fresh mount. If still signed out after the reload, redirect to sign-in.
const RELOAD_KEY = '__marginpal_sync_reload';

function SatelliteAwareRedirect() {
  if (!IS_SATELLITE) return <RedirectToSignIn />;

  useEffect(() => {
    const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY);

    if (!alreadyReloaded) {
      sessionStorage.setItem(RELOAD_KEY, '1');
      setTimeout(() => window.location.reload(), 1500);
    } else {
      sessionStorage.removeItem(RELOAD_KEY);
      window.location.href = `https://accounts.${CLERK_DOMAIN}/sign-in?redirect_url=${encodeURIComponent(window.location.origin)}`;
    }
  }, []);

  return <LoadingSpinner />;
}

export default function App() {
  const { loaded } = useClerk();

  if (!loaded) return <LoadingSpinner />;

  return (
    <>
      <SentryUserSync />
      <AppRoutes />
    </>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/sign-in/*" element={<SignInPage />} />
      <Route path="/sign-up/*" element={<SignUpPage />} />
      <Route path="/quote-response" element={<QuoteResponsePage />} />

      <Route
        path="/*"
        element={
          <>
            <SignedIn>
              <Routes>
                {/* /onboarding lives OUTSIDE RequireOrg — first-time
                    signups have no org yet and need this page to create
                    one. The page itself bounces existing-org users back
                    to /dashboard so direct navigation can't be misused. */}
                <Route path="onboarding" element={<OnboardingPage />} />
                <Route
                  path="*"
                  element={
                    <RequireOrg>
                      <Routes>
                        <Route path="subscribe" element={<SubscribePage />} />
                        <Route
                          path="*"
                          element={
                            <RequireSubscription>
                              <Routes>
                                <Route path="/" element={<AppLayout />}>
                                  <Route index element={<Navigate to="/dashboard" replace />} />
                                  <Route path="dashboard" element={<DashboardPage />} />
                                  <Route path="customers" element={<CustomersPage />} />
                                  <Route path="customers/:id" element={<ViewCustomerPage />} />
                                  <Route path="quotes"    element={<QuotesPage />} />
                                  <Route path="quotes/:id" element={<ViewQuotePage />} />
                                  <Route path="jobs"      element={<JobsPage />} />
                                  <Route path="jobs/:id"  element={<ViewJobPage />} />
                                  <Route path="settings"  element={<SettingsPage />} />
                                  <Route path="help"      element={<HelpPage />} />
                                </Route>
                                <Route path="*" element={<Navigate to="/dashboard" replace />} />
                              </Routes>
                            </RequireSubscription>
                          }
                        />
                      </Routes>
                    </RequireOrg>
                  }
                />
              </Routes>
            </SignedIn>
            <SignedOut>
              <SatelliteAwareRedirect />
            </SignedOut>
          </>
        }
      />
    </Routes>
  );
}
